<script setup lang="ts">
import greenAddIcon from '@/assets/icons/green/add.svg'
import greyAddIcon from '@/assets/icons/grey/add.svg'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useToast } from 'vue-toastification'
const props = defineProps<{
  modelValue?: unknown
  disableAdd: boolean
  disableInput: boolean
  error?: string
  list: string[]
  preValidator?: (v: string) => boolean
  postValidator?: (v: string) => Promise<boolean>
  placeholder?: string
}>()

const toast = useToast()
const MAX_LIST_LENGTH_DUE_TO_AWS_LIMIT = 40
const emit = defineEmits(['update:modelValue', 'input', 'submit'])
const chipList = ref<string[]>([])
const chipInput = ref('')

const emailRegex =
  /^(?!\.)(?!.*\.\.)[A-Za-z0-9.!#$%&'*+/=?^_`{|}~-]+(?<!\.)@(?:(?:[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?)\.)+[A-Za-z]{2,}$/
const defaultPreValidator = (v: string) => emailRegex.test(v.trim())

const value = computed({
  get() {
    return chipInput.value
  },
  set(value) {
    chipInput.value = value
    emit('update:modelValue', value)
  },
})

const errorString = ref('')
const { t } = useI18n()

async function addChip(value: string) {
  if (!value) return Promise.resolve()
  const validator = props.preValidator ?? defaultPreValidator
  if (!validator(value)) {
    errorString.value = t('general.invalidEmail')
    return
  }
  const alreadyExists =
    chipList.value.find((s) => s == value) ||
    props.list.find((s) => s === value)
  if (alreadyExists) {
    errorString.value = t('general.duplicateEmail')
    return
  }

  if (props.postValidator) {
    const ok = await props.postValidator(value)
    if (!ok) {
      errorString.value = t('general.invalidEmail')
      return
    }
  }

  errorString.value = ''
  chipList.value.push(value)
  chipInput.value = ''
}

async function handleKeyUp(e: KeyboardEvent) {
  const inputValue = (e.target as HTMLInputElement)?.value
  if (e.key !== ';') return
  const valueWithoutSemiColon = inputValue
    .split('')
    .filter((c) => c !== ';')
    .join('')
  chipInput.value = valueWithoutSemiColon

  addChip(valueWithoutSemiColon)
}

async function handlePaste(e: Event) {
  e.preventDefault()
  const textFromEvent =
    (e as ClipboardEvent).clipboardData?.getData('text') ?? null
  const clipboardData = textFromEvent ?? (await navigator.clipboard.readText())
  const emailsToAdd = clipboardData
    .split(/[;,\n\s]+/)
    .map((s) => s.trim())
    .filter(Boolean)

  for (const email of emailsToAdd) {
    if (
      props.list.length + chipList.value.length + 1 >
      MAX_LIST_LENGTH_DUE_TO_AWS_LIMIT
    ) {
      toast.error(
        t('corporate.member.addMembersDialog.maxInviteNumberAchieved', {
          number: MAX_LIST_LENGTH_DUE_TO_AWS_LIMIT,
        })
      )
      break
    }
    addChip(email)
  }
}
async function handleSubmit() {
  if (chipInput.value.trim()) {
    await addChip(chipInput.value)
  }
  if (errorString.value) return
  const allChips = [...props.list, ...chipList.value]
  emit('submit', allChips)
  chipList.value = []
  chipInput.value = ''
  errorString.value = ''
}

watch(
  () => props,
  () => {
    if (!props.list.length) chipList.value = []
  }
)

const runPreValidator = (v: string) =>
  (props.preValidator ?? defaultPreValidator)(v.trim())

watch(chipInput, (val) => {
  const v = (val ?? '').trim()
  if (!v) {
    errorString.value = ''
    return
  }
  if (runPreValidator(v)) {
    errorString.value = ''
  }
})
</script>
<template>
  <v-container class="pa-0" fluid>
    <v-sheet
      rounded
      class="w-100 d-flex align-center"
      :class="errorString ? 'border-error' : 'border-thin'"
      style="flex-wrap: wrap; gap: 6px; padding: 6px 8px"
    >
      <v-chip
        v-for="chip in chipList"
        :key="chip"
        closable
        style="white-space: nowrap"
      >
        {{ chip }}
      </v-chip>

      <input
        v-model="value"
        class="v-field__input styleText chipinput"
        :placeholder="placeholder"
        :disabled="disableInput"
        :aria-invalid="!!errorString"
        inputmode="email"
        @keyup="handleKeyUp"
        @keyup.delete="() => chipInput == '' && chipList.pop()"
        @keyup.enter="handleSubmit"
        @paste="handlePaste"
      />

      <span class="d-flex align-center ml-2">
        <v-btn
          icon
          variant="text"
          :class="{ 'text-success': chipInput && !disableAdd }"
          :disabled="!chipInput || disableAdd"
          @click="handleSubmit"
        >
          <v-img
            :src="chipInput && !disableAdd ? greenAddIcon : greyAddIcon"
            width="24"
          />
        </v-btn>
      </span>
    </v-sheet>
    <div
      class="text-caption mt-1"
      style="color: rgb(var(--v-theme-error)); margin-left: 0.5rem"
    >
      {{ errorString }}
    </div>
  </v-container>
</template>
<style>
.border-error {
  border: 1px solid rgb(var(--v-theme-error)) !important;
}
.chipinput {
  flex: 1 1 210px;
  padding: 0 0.2rem;
}
@media (max-width: 600px) {
  .v-container.v-locale--is-ltr {
    padding: 0 0 !important;
  }
}
</style>
